import type React from 'react';
import { useRef, useEffect } from 'react';

import clsx from 'clsx';

import type { BaseComponentProps } from '@coursera/cds-common';

import ActionOverflow from '@core/ActionOverflow';
import type { Item, ItemsCollection } from '@core/utils';

import type { CommonProps } from './ActionButton';
import getActionButtonGroupCss, { classes } from './getActionButtonGroupCss';

const getLastVisibleItemId = (
  items: Item<HTMLButtonElement>[],
  itemsVisibility: CommonProps['itemsVisibility']
) => {
  return items.reduce((lastVisibleItemId, item) => {
    return itemsVisibility[item.id] ? item.id : lastVisibleItemId;
  }, items[0].id);
};

const hasHiddenItems = (itemsVisibility: CommonProps['itemsVisibility']) => {
  return Object.values(itemsVisibility).some((v) => !v);
};

type FocusRef = {
  wasOnButtonThatIsNowHidden?: boolean;
  wasOnActionOverflowThatIsNowHidden?: boolean;
};

export type Props = BaseComponentProps<'div'> & {
  direction?: 'row' | 'column';
  children?: React.ReactNode;
  collection: ItemsCollection<HTMLButtonElement, CommonProps>;
  setItemsVisibility: React.Dispatch<
    React.SetStateAction<Record<string, boolean>>
  >;
};

const ButtonsOverflow = (props: Props) => {
  const {
    children,
    direction = 'row',
    collection,
    setItemsVisibility,
    ...rest
  } = props;

  const containerRef = useRef<HTMLDivElement>(null);
  const actionOverflowRef = useRef<HTMLButtonElement>(null);
  const actionOverflowMenuRef = useRef<HTMLOListElement>(null);
  const focusRef = useRef<FocusRef>({});

  const { items, commonProps } = collection;

  const handleIntersection = (entries: IntersectionObserverEntry[]) => {
    setItemsVisibility((prev) => {
      const visibility = { ...prev };
      let visibilityChanged = false;

      for (const entry of entries) {
        const visible = entry.isIntersecting;

        // @ts-expect-error `entry.target` is `HTMLButton` and it always has data-cds-overflow-id attribute
        const id = entry.target.dataset.cdsOverflowId;
        if (prev[id] !== visible) {
          visibility[id] = visible;
          visibilityChanged = true;

          focusRef.current.wasOnButtonThatIsNowHidden =
            // @ts-expect-error: we are asserting that it exists
            !visible && document.activeElement?.dataset?.cdsOverflowId === id;
        }
      }

      if (!visibilityChanged) {
        return prev;
      }

      const focusIsInActionOverflow =
        actionOverflowRef.current === document.activeElement ||
        actionOverflowMenuRef.current?.contains?.(document.activeElement);

      focusRef.current.wasOnActionOverflowThatIsNowHidden =
        !hasHiddenItems(visibility) && focusIsInActionOverflow;

      return visibility;
    });
  };

  /**
   * Observe visibility of action buttons and hide them if they do not fit
   */
  useEffect(() => {
    const observer = new IntersectionObserver(handleIntersection, {
      root: containerRef?.current,
      threshold: 1,
    });

    items?.forEach((item) => {
      observer.observe(item.element);
    });

    return () => {
      observer.disconnect();
    };
  }, [items]);

  /**
   * Observe visibility of ActionOverflow and hide a button before it if ActionOverflow does not fit
   */
  useEffect(() => {
    if (!actionOverflowRef.current) {
      return;
    }

    const observer = new IntersectionObserver(handleIntersection, {
      root: containerRef.current,
      threshold: 1,
    });

    observer.observe(actionOverflowRef.current);

    return () => {
      observer.disconnect();
    };
  }, [commonProps.itemsVisibility]);

  /**
   * Reposition the focus after all updates if necessary:
   *
   * If the focus was on an action button that is now hidden, the focus moves to the ActionOverflow trigger.
   * If the focus was inside of the menu and the button group fully expands, focus moves to the first element in the group.
   */
  useEffect(() => {
    if (focusRef.current.wasOnButtonThatIsNowHidden) {
      actionOverflowRef.current?.focus();
    }

    if (focusRef.current.wasOnActionOverflowThatIsNowHidden) {
      items[0].element.focus();
    }

    focusRef.current = {};
  }, [commonProps.itemsVisibility, items]);

  return (
    <div
      ref={containerRef}
      className={clsx({
        [classes.column]: direction === 'column',
        [classes.ghostVariant]:
          commonProps.variant === 'ghost' ||
          commonProps.variant === 'ghostInvert',
        [classes.small]: commonProps.size === 'small',
      })}
      css={getActionButtonGroupCss}
      role="group"
      {...rest}
    >
      {children}
      {hasHiddenItems(commonProps.itemsVisibility) && (
        <ActionOverflow
          ref={actionOverflowRef}
          data-cds-overflow-id={getLastVisibleItemId(
            items,
            commonProps.itemsVisibility
          )}
          invert={commonProps.variant === 'ghostInvert'}
          menuRef={actionOverflowMenuRef}
          size={commonProps.size}
        >
          {items
            .filter((item) => !commonProps.itemsVisibility[item.id])
            .map((item) => (
              <ActionOverflow.Item
                key={item.id}
                {...collection.props[item.id]}
              />
            ))}
        </ActionOverflow>
      )}
    </div>
  );
};

export default ButtonsOverflow;
